import Vue from 'vue';
import {
    createRule,
    updateRule,
    runRule,
    deleteRule,
    fetchAutomationRules,
    fetchAgg,
    reorderRules,
} from '@/vuex/modules/automation-rules/automation-rules.api';
import { formatRule, formatRules } from '@/vuex/modules/automation-rules/automation-rules.utils';
import { runAggregationOnce, SUB_PRODUCT_AREAS, PRODUCT_AREAS } from '@/utils/aggregation';
import { operators as o } from '@pendo/aggregations';
import { v4 as uuid } from 'uuid';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';

const state = {
    openRule: {},
    openAggregation: null,
    error: {},
    configError: false,
    map: {},
    matchingItemsCount: null,
    uuid: {
        drawerCount: undefined,
    },
};

const mutations = {
    setOpenRule (state, { rule }) {
        state.openRule = rule;
    },
    setOpenAggregation (state, { aggregation }) {
        state.openAggregation = aggregation;
    },
    setError (state, { error }) {
        state.error = error;
    },
    setConfigError (state, { error }) {
        state.configError = error;
    },
    setMatchingItemsCount (state, { count }) {
        state.matchingItemsCount = count;
    },
    setMap (state, { map }) {
        state.map = map;
    },
    setPromise (state, { promise }) {
        state.promise = promise;
    },
    setUuid (state, { key }) {
        state.uuid[key] ??= uuid();
    },
    deleteRule (state, { ruleId }) {
        Vue.delete(state.map, ruleId);
    },
};

const actions = {
    async saveAndRunAutomationRule ({ state, commit }, { rule, run }) {
        let restoreSavedRule;
        try {
            rule.triggerConfig = 'vocItemFeedbackCreated';
            let automationRule;

            if (run === true) {
                rule.applyMatchingItems = true;
            }

            if (rule.id) {
                // Save a copy of the rule in case the save fails
                restoreSavedRule = { ...state.map[rule.id] };
                // Update the rule in the store so the UI updates immediately
                commit('setMap', { map: { ...state.map, [rule.id]: rule } });
                automationRule = await updateRule(rule);
            } else {
                automationRule = await createRule(rule);
            }

            if (run === true && automationRule?.id) {
                try {
                    automationRule = await runRule(automationRule.id);
                } catch (error) {
                    error.runFailed = true;
                    throw error;
                }
            }

            const formattedRule = formatRule(automationRule);

            commit('setMap', { map: { ...state.map, [automationRule.id]: formattedRule } });

            if (!restoreSavedRule || restoreSavedRule.enabled !== automationRule.enabled) {
                const { visitor, account } = window.pendo.getSerializedMetadata();
                window.pendo.updateOptions({
                    visitor,
                    account: {
                        ...account,
                        activeAutomationRuleCount: Object.values(state.map).filter((rule) => rule.enabled).length,
                    },
                });
            }

            return automationRule;
        } catch (error) {
            if (restoreSavedRule) {
                // Restore the rule in the store
                commit('setMap', { map: { ...state.map, [restoreSavedRule.id]: restoreSavedRule } });
            }

            commit('setError', { error });
            throw error;
        }
    },
    async fetchAutomationRules ({ commit }) {
        try {
            const { rules, canReorder } = await fetchAutomationRules();
            const formattedRules = formatRules(rules);
            commit('setMap', { map: keyBy(formattedRules, 'id') });

            return { rules, canReorder };
        } catch (error) {
            commit('setError', { error });
            throw error;
        }
    },
    fetchAggregation ({ state, commit, getters }) {
        commit('setConfigError', { error: false });
        commit('setUuid', { key: 'drawerCount' });

        return fetchAgg({ conditionConfig: getters.conditionConfig, actionConfig: getters.actionConfig })
            .then((agg) => {
                const aggregation = o.pipeline(agg);
                commit('setOpenAggregation', { aggregation });

                const { VOC } = PRODUCT_AREAS;
                const { CAPTURE } = SUB_PRODUCT_AREAS;

                const aggregationObj = {
                    name: 'AutomationAdHocAgg',
                    responseFormat: 'rows',
                    productArea: VOC,
                    subProductArea: CAPTURE,
                    build: () => aggregation,
                };

                return runAggregationOnce(aggregationObj, {}, state.uuid.drawerCount)
                    .then((aggResponse) => {
                        commit('setMatchingItemsCount', { count: aggResponse?.length || 0 });
                        commit('setConfigError', { error: false });
                    })
                    .catch((err) => handleAggError(err));
            })
            .catch((err) => handleConfigAndActionError(err));

        function handleConfigAndActionError (err) {
            commit('setMatchingItemsCount', { count: null });
            commit('setConfigError', { error: err?.response?.data || true });
            commit('setOpenAggregation', { aggregation: null });
        }

        function handleAggError () {
            commit('setMatchingItemsCount', { count: null });
            commit('setConfigError', { error: { kind: 'agg', message: 'aggregation failed to run' } });
            commit('setOpenAggregation', { aggregation: null });
        }
    },
    updateOpenRule ({ commit }, { rule }) {
        commit('setOpenRule', { rule });
    },
    deleteAutomationRule ({ commit }, { ruleId }) {
        return deleteRule(ruleId)
            .then(() => {
                commit('deleteRule', { ruleId });
            })
            .catch((error) => {
                throw error;
            });
    },
    reorderAutomationRules ({ state, commit }, { automationRuleIds, orderedGroupId }) {
        let displayOrder = 1;
        const originalMap = { ...state.map };

        const map = automationRuleIds.reduce((acc, id) => {
            acc[id] = {
                ...state.map[id],
                order: displayOrder++,
            };

            return acc;
        }, {});
        commit('setMap', { map });

        return reorderRules({ automationRuleIds, orderedGroupId }).catch((error) => {
            commit('setMap', { map: originalMap });

            throw error;
        });
    },
};

const getters = {
    rulesList: (state) =>
        Object.values(state.map)
            .map((rule, index) => ({ ...rule, order: rule.order || index + 1 }))
            .sort((a, b) => a.order - b.order),
    conditionConfig: (state) => {
        const conditionConfig = {
            data: [],
        };
        if (state.openRule?.conditionConfig?.data?.length) {
            state.openRule.conditionConfig.data.forEach((andRule) => {
                const subConfig = andRule.reduce((acc, orRule) => {
                    if (
                        orRule.config?.operator &&
                        orRule.config?.negative !== undefined &&
                        (orRule.config?.valueListString?.length || orRule.config?.valueString !== undefined)
                    ) {
                        acc.push(orRule);
                    }

                    return acc;
                }, []);

                if (!isEmpty(subConfig)) {
                    conditionConfig.data.push(subConfig);
                }
            });
        }

        return conditionConfig;
    },
    conditionConfigFilledOut: (state) => {
        if (isEmpty(state.openRule?.conditionConfig?.data)) {
            return true;
        }

        return state.openRule.conditionConfig.data.every((andRule) => {
            return andRule.every((orRule) => {
                return (
                    orRule.config?.operator &&
                    orRule.config?.negative !== undefined &&
                    (orRule.config?.valueString !== undefined || orRule.config?.valueListString !== undefined)
                );
            });
        });
    },
    actionConfig: (state) => {
        return state.openRule?.actionConfig;
    },
    actionConfigFilledOut: (state) => {
        if (isEmpty(state.openRule?.actionConfig)) {
            return false;
        }

        return Object.keys(state.openRule.actionConfig).every((key) => {
            const value = state.openRule.actionConfig[key];

            return !isEmpty(value) || key === 'ownedByUserId';
        });
    },
    canSaveRuleTooltipString: (state, getters) => {
        if (!(state.openRule?.name?.trim().length > 0 && state.openRule?.name?.trim().length <= 100)) {
            return 'Rule name needed';
        }

        if (!(state.openRule?.description?.length <= 250)) {
            return 'Description is over 250 characters';
        }

        if (!getters.conditionConfigFilledOut) {
            return 'Config not fully filled out';
        }

        if (!getters.actionConfigFilledOut) {
            return 'Missing "Then" config';
        }

        if (state.configError) {
            return state.configError.message;
        }

        return '';
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
